import { Check, ChevronRight, Circle, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { goalBadge } from '../../../../lib/goal-status';
import { findGoalForCard, goalCardChip, goalCardPhase, type GoalCardData } from '../../../../lib/goal-card';
import type { WebGoal, WebGoalTask } from '../../../../lib/goals';
import { useWorkspaceGoals } from '../../../../lib/goals';
import { useTranslation } from '../../../../lib/i18n';
import { useMyAgents } from '../../../../lib/my-agents';
import { taskStatusBadge } from '../../../../lib/task-status';
import { isNotStartedTask } from '../../../../lib/task-schedule';
import { awaitsDecision, showsLiveMarker } from '../../../../lib/agent-task-buckets';
import { ENTITY_TYPE, useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { AgentDot } from '../../../AgentDot';
import { fireConfetti } from '../../confetti';
import { LiveGoalDetailsModal } from '../../goals/GoalDetailsModal';
import { useTaskSidePanel } from '../../task-side-panel/TaskSidePanelContext';
import { Badge, Button, Card, ProgressBar, TextButton } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import styles from './ChatGoalCard.module.css';

/**
 * The goal in the chat, as a compact live view (Figma 19472:22308 → 19472:47720, and 19472:21656 when it
 * lands). The metadata only names the goal; what it draws comes from the live goals query, which the goal
 * and task duplexers refetch — so a task finishing flips its row, its badge and the bar without any new
 * message. See `lib/goal-card.ts` for the phases.
 *
 * Kick off SENDS a chat message and does nothing else: starting the goal is the maestro's move, the same
 * as when the owner types "yes". Web places the card under the reply's words, where the answer to "ready
 * to set the team to work?" belongs, and both phones do the same.
 */
export function ChatGoalCard({
  card,
  conversationId,
  onKickOff,
  canSend = true,
}: {
  card: GoalCardData;
  conversationId?: string;
  /** Sends the kick-off line as a user turn. Absent on a channel-locked thread → no button. */
  onKickOff?: (text: string) => void;
  canSend?: boolean;
}) {
  const state = useWorkspaceGoals(undefined);
  const goal = state.status === 'ready' ? findGoalForCard(state.goals, card) : undefined;
  return card.variant === 'completed' ? (
    <CompletedGoalCard card={card} goal={goal} conversationId={conversationId} />
  ) : (
    <PlanGoalCard card={card} goal={goal} conversationId={conversationId} onKickOff={onKickOff} canSend={canSend} />
  );
}

function useSeeFullPlan(goal: WebGoal | undefined, conversationId: string | undefined, variant: GoalCardData['variant']) {
  const { openModal } = useTaskSidePanel();
  const { logPageClickBi } = useGenericBi();
  if (!goal) return undefined;
  return () => {
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'goal-card-see-full-plan', ...(conversationId ? { conversation_id: conversationId } : {}) },
      { additionalFields: { goal_id: goal.id, variant } },
    );
    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
  };
}

function useGoalCardSeen(card: GoalCardData, goal: WebGoal | undefined, conversationId: string | undefined) {
  return useItemSeenBi('goal_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: { variant: card.variant, phase: goalCardPhase(goal), goal_slug: card.slug },
  });
}

function PlanGoalCard({
  card,
  goal,
  conversationId,
  onKickOff,
  canSend,
}: {
  card: GoalCardData;
  goal: WebGoal | undefined;
  conversationId?: string;
  onKickOff?: (text: string) => void;
  canSend: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  // Once pressed the button stays busy until the goal leaves KICKOFF and the button goes for good — a
  // second press would send a second "kick off" while the first is still being acted on.
  const [kickedOff, setKickedOff] = useState(false);
  const phase = goalCardPhase(goal);
  const chip = goalCardChip(card);
  const seeFullPlan = useSeeFullPlan(goal, conversationId, card.variant);
  const seenRef = useGoalCardSeen(card, goal, conversationId);
  const title = goal?.title?.trim() || card.title;
  const percent = goal?.percent ?? 0;
  const progressLabel = goal ? `${percent}% ${t(goalBadge(goal).labelKey)}` : undefined;
  const rows: { key: string; title: string; agentKey: string; agentName: string; task?: WebGoalTask }[] = goal
    ? goal.tasks.map((task) => {
        // The roster may not have loaded yet; the snapshot named the seat when the plan was laid out.
        const agentName =
          team.find((member) => member.id === task.agentId)?.name?.trim() || card.tasks?.find((planned) => planned.title === task.title)?.agent || '';
        return { key: task.id, title: task.title?.trim() || t('tasks.untitledTask'), agentKey: task.agentId || agentName, agentName, task };
      })
    : (card.tasks ?? []).map((task, index) => ({ key: `${index}`, title: task.title, agentKey: task.agent ?? '', agentName: task.agent ?? '' }));

  const onKickOffClick = () => {
    if (!onKickOff) return;
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'goal-card-kick-off', ...(conversationId ? { conversation_id: conversationId } : {}) },
      { additionalFields: { goal_id: goal?.id, goal_slug: card.slug } },
    );
    setKickedOff(true);
    onKickOff(t('chat.goalCard.kickoffMessage'));
  };

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={title} data-testid="chat-goal-card" data-phase={phase}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h3 className={styles.title} dir="auto">
            {title}
          </h3>
          {chip && <span className={styles.chip}>{t(`chat.opportunityCard.chip.${chip}`)}</span>}
        </div>
        {progressLabel && (
          <div className={styles.progress}>
            <span className={styles.progressLabel}>{progressLabel}</span>
            <ProgressBar progress="success" value={percent} decorative />
          </div>
        )}
        <ol className={styles.tasks}>
          {rows.map((row, index) => (
            <li key={row.key} className={styles.task}>
              <div className={styles.taskHead}>
                {phase === 'running' && row.task && <TaskMark task={row.task} />}
                <span className={styles.taskTitle} dir="auto">
                  {phase !== 'running' && <span className={styles.taskIndex}>{`${index + 1}/${rows.length} `}</span>}
                  {row.title}
                </span>
                {phase === 'running' && row.task && <TaskBadge task={row.task} />}
              </div>
              {row.agentName && (
                <div className={styles.agent}>
                  <AgentDot id={row.agentKey} size={15} />
                  <span className={styles.agentName}>{row.agentName}</span>
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
      {phase === 'kickoff' && onKickOff && (
        <Button size="medium" className={styles.kickoff} onClick={onKickOffClick} loading={kickedOff} disabled={!canSend || kickedOff}>
          {t('chat.goalCard.kickoff')}
        </Button>
      )}
      {phase === 'running' && seeFullPlan && <SeeFullPlan onClick={seeFullPlan} />}
    </Card>
  );
}

function TaskMark({ task }: { task: WebGoalTask }) {
  if (task.status === 'COMPLETED') return <Check aria-hidden size={16} className={styles.taskMark} />;
  // `showsLiveMarker`, not `isInMotion`: a spinning loader on a row the reader is being asked about
  // contradicts the ask. The live-run FACT can still be true there (the contract keeps it true for
  // `paused-with-live-run` and for a decision held over a verdict) — it is the MARKER that must not show.
  if (showsLiveMarker(task)) return <LoaderCircle aria-hidden size={16} className={`${styles.taskMark} ${styles.taskMarkBusy}`} />;
  return <Circle aria-hidden size={16} className={`${styles.taskMark} ${styles.taskMarkIdle}`} />;
}

function TaskBadge({ task }: { task: WebGoalTask }) {
  const { t } = useTranslation();
  // `awaitingDecision` is not optional polish: the backend never writes a status on a block, so a blocked
  // goal task is PENDING — which this function, seeing only a status string, badges "In progress" while
  // the Tasks screen's own card badges the same task "Action needed". The plan payload carries the
  // prompts for exactly this (`/api/goals`).
  const badge = taskStatusBadge(task.status, {
    awaitingDecision: awaitsDecision(task),
    notStarted: isNotStartedTask(task, Date.now()),
  });
  return <Badge variant={badge.variant} label={t(badge.labelKey)} className={styles.taskBadge} />;
}

function SeeFullPlan({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <TextButton size="small" className={styles.seeFullPlan} endIcon={<ChevronRight aria-hidden size={16} className="dir-flip" />} onClick={onClick}>
      {t('chat.goalCard.seeFullPlan')}
    </TextButton>
  );
}

function CompletedGoalCard({ card, goal, conversationId }: { card: GoalCardData; goal: WebGoal | undefined; conversationId?: string }) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);
  const seeFullPlan = useSeeFullPlan(goal, conversationId, card.variant);
  const seenRef = useGoalCardSeen(card, goal, conversationId);

  // The milestone card's burst, verbatim and on the same terms: once per mount, from the check, and not at
  // all under reduced motion.
  useEffect(() => {
    const canvas = canvasRef.current;
    const mark = markRef.current;
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !canvas || !mark) return;
    const timer = window.setTimeout(() => fireConfetti(canvas, mark), 300);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <Card component="section" ref={seenRef} className={`${styles.card} ${styles.completed}`} data-testid="chat-goal-card" data-phase="completed">
      <canvas ref={canvasRef} aria-hidden className={styles.confetti} />
      <div className={styles.completedHead}>
        <span ref={markRef} aria-hidden className={styles.completedMark}>
          <Check size={16} />
        </span>
        <div className={styles.completedText}>
          <span className={styles.completedLabel}>{t('chat.goalCard.completed')}</span>
          <h3 className={styles.title} dir="auto">
            {goal?.title?.trim() || card.title}
          </h3>
        </div>
      </div>
      {seeFullPlan && <SeeFullPlan onClick={seeFullPlan} />}
    </Card>
  );
}
